import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { ClaimActions } from '@/components/common/ClaimActions';
import { formatDate } from '@/lib/utils/date.utils';
import { isPdfUrl } from '@/lib/utils/media.utils';
import { PARTNER_OFFERS_STRINGS as S } from '../partner-offers.constants';

export interface OfferPreviewCardProps {
  title: string;
  subtitle: string;
  description: string;
  /** ISO date string from the form's date input, or '' while unset. */
  expiresAt: string;
  /** The form's current Claim Link and Email (FR-114), or '' while unset. */
  claimLinkUrl: string;
  claimEmail: string;
  /** The offer's cover as the form currently has it (a just-picked file or the saved one); a PDF is not shown. */
  coverPhotoUrl?: string | null;
  /** The partner's own profile logo - an offer has no logo of its own. */
  partnerLogoUrl?: string | null;
  partnerName: string;
  /** Two-letter initials, for a partner with no logo uploaded. */
  partnerInitials: string;
}

/**
 * Live preview of the offer as a member will meet it (MYN-106 AC2), rendered
 * beside the create/edit form and updating as the partner types.
 *
 * Takes plain values rather than the form object: it is a presentational card
 * with no react-hook-form knowledge, so the Perks page (MYN-107) can render it
 * from a fetched offer without a form anywhere in scope. Promote it to
 * `components/common/` at that second use, per the promotion rule - it is a
 * single feature's component until then.
 *
 * Every field is optional in practice because the partner is mid-typing, so
 * each one falls back to a placeholder that reads as "not filled in yet"
 * rather than collapsing the layout - the point of a live preview is that it
 * keeps its shape while the content arrives.
 */
export function OfferPreviewCard({
  title,
  subtitle,
  description,
  expiresAt,
  claimLinkUrl,
  claimEmail,
  coverPhotoUrl,
  partnerLogoUrl,
  partnerName,
  partnerInitials,
}: OfferPreviewCardProps) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-p6 text-slate font-semibold">{S.PREVIEW_HEADING}</p>

      <div className="rounded-card border-divider shadow-card flex flex-col gap-3 border bg-white p-5">
        {coverPhotoUrl && !isPdfUrl(coverPhotoUrl) && (
          <img src={coverPhotoUrl} alt="" className="aspect-video w-full rounded-card object-cover" />
        )}
        <div className="flex items-center gap-3">
          <Avatar photoUrl={partnerLogoUrl ?? undefined} initials={partnerInitials} size={40} />
          <span className="text-p6 text-slate">{partnerName || S.PREVIEW_PARTNER_PLACEHOLDER}</span>
        </div>

        <div>
          <p className="text-navy text-p3 font-bold">{title || S.PREVIEW_TITLE_PLACEHOLDER}</p>
          {subtitle && <p className="text-p6 text-slate mt-0.5">{subtitle}</p>}
        </div>

        <p className="text-p6 text-slate whitespace-pre-line">
          {description || S.PREVIEW_DESCRIPTION_PLACEHOLDER}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          {/* MYN-104 AC7: an offer with no end date runs until it is
              deactivated, so the preview says that rather than leaving a gap
              the partner might read as a missing field. */}
          <Badge variant="chip" size="sm">
            {expiresAt ? `Expires ${formatDate(expiresAt)}` : S.NO_EXPIRY}
          </Badge>
        </div>

        {/* The same layout a member meets, non-interactive (FR-114): link only, email only, or both. */}
        <ClaimActions claimLinkUrl={claimLinkUrl} claimEmail={claimEmail} preview />
      </div>

      <p className="text-slate text-[11px]">{S.PREVIEW_HINT}</p>
    </div>
  );
}
